import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const OPD_STEPS = ["Registration", "Consultation", "Lab & Pharmacy", "Billing", "Follow-up"];

const EASE = [0.16, 1, 0.3, 1];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, ease: EASE, delay },
});

const Tag = ({ children }) => (
  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-slate-500">
    {children}
  </span>
);

export default function Products() {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="scroll-mt-24 border-y border-slate-200/70 bg-slate-50/70 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-14 grid grid-cols-1 items-end gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <motion.p
              {...reveal()}
              className="mb-5 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand"
            >
              <span className="h-px w-10 bg-brand" />
              Products &amp; Services
            </motion.p>
            <motion.h2
              {...reveal(0.05)}
              className="font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]"
            >
              A suite built on one promise.
            </motion.h2>
          </div>
          <motion.p
            {...reveal(0.1)}
            className="max-w-sm text-base leading-relaxed text-slate-600 lg:col-span-4 lg:justify-self-end lg:text-right"
          >
            Three products and one consulting practice — each one built on
            the same zero-defect engineering discipline.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
          <motion.div
            {...reveal()}
            data-testid="product-card-jisll-opd"
            className="group relative flex flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-8 transition-all duration-200 hover:-translate-y-1 hover:border-brand/40 hover:shadow-[0_24px_56px_rgba(0,82,255,0.1)] md:col-span-12 lg:col-span-8"
          >
            <div className="bg-grid-card absolute inset-0 opacity-60" />
            <div className="relative flex flex-1 flex-col">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">
                    Flagship Product · Healthcare
                  </p>
                  <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                    JISLL OPD
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Tag>Healthcare</Tag>
                  <Tag>Outpatient</Tag>
                  <Tag>End-to-End</Tag>
                </div>
              </div>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
                An end-to-end outpatient model — one system that carries a
                patient through the entire OPD journey, from first registration
                to follow-up.
              </p>
              <div className="mt-10" data-testid="opd-journey-stepper">
                <div className="relative flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
                  <div className="absolute bottom-2 left-[13px] top-2 w-px bg-slate-200 sm:bottom-auto sm:left-0 sm:right-0 sm:top-[13px] sm:h-px sm:w-auto" />
                  {OPD_STEPS.map((step, i) => (
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.08 * i, ease: EASE }}
                      className="relative flex items-center gap-3 sm:flex-col sm:items-center sm:gap-3"
                    >
                      <span
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-mono text-[10px] ${
                          i === 0
                            ? "border-brand bg-brand text-white"
                            : "border-slate-300 bg-white text-slate-500"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">
                        {step}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            {...reveal(0.08)}
            data-testid="product-card-jisll-tracker"
            className="group relative flex min-h-[340px] flex-col justify-end overflow-hidden rounded-3xl border border-slate-200/80 md:col-span-6 lg:col-span-4"
          >
            <img
              src="https://images.unsplash.com/photo-1758691463203-cce9d415b2b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Healthcare team using JISLL Tracker on a tablet"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/35 to-ink/10" />
            <div className="relative p-8">
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full border border-white/25 bg-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-white backdrop-blur-sm">
                  Healthcare
                </span>
                <span className="rounded-full border border-white/25 bg-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-white backdrop-blur-sm">
                  Field Teams
                </span>
              </div>
              <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight text-white">
                JISLL Tracker
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-200">
                Built for healthcare marketing employees — visits, follow-ups
                and field activity, tracked in one place.
              </p>
            </div>
          </motion.div>

          <motion.div
            {...reveal(0.04)}
            data-testid="product-card-kappawisevet"
            className="group relative flex min-h-[340px] flex-col justify-end overflow-hidden rounded-3xl border border-slate-200/80 md:col-span-6 lg:col-span-4"
          >
            <img
              src="https://images.unsplash.com/photo-1554412663-7b99cf315535?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="KYC and KYB onboarding workflows in KappawiseVet"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/35 to-ink/10" />
            <div className="relative p-8">
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full border border-white/25 bg-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-white backdrop-blur-sm">
                  KYC · KYB
                </span>
                <span className="rounded-full border border-white/25 bg-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-white backdrop-blur-sm">
                  RegTech
                </span>
              </div>
              <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight text-white">
                KappawiseVet
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-200">
                KYC and KYB onboarding on a workflow model — dynamic business
                rule engine, dynamic case definitions, risk assessment and
                ongoing monitoring powered with Solytics.
              </p>
            </div>
          </motion.div>

          <motion.div
            {...reveal(0.1)}
            data-testid="product-card-it-consulting"
            className="group flex flex-col rounded-3xl bg-ink p-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_24px_56px_rgba(15,23,42,0.35)] md:col-span-6 lg:col-span-8"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-slate-300">
                    Advisory
                  </span>
                  <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-slate-300">
                    Delivery Teams
                  </span>
                </div>
                <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
                  Custom IT Consulting
                </h3>
              </div>
              <p className="font-mono text-[9px] uppercase leading-relaxed tracking-[0.16em] text-brand">
                Backed by 14+ years of
                <br />
                enterprise architecture
              </p>
            </div>
            <p className="mt-4 max-w-xl flex-1 text-sm leading-relaxed text-slate-300 sm:text-base">
              Dedicated architecture and delivery teams for cloud, security
              and modernization programs — scoped, staffed and SLA-backed.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-2 border-t border-white/10 pt-6 sm:grid-cols-4">
              {["Cloud & DevOps", "Modernization", "Cyber Resilience", "AI & Testing"].map(
                (d) => (
                  <span
                    key={d}
                    className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-300"
                  >
                    <span className="h-1 w-1 rounded-full bg-brand" />
                    {d}
                  </span>
                ),
              )}
            </div>
            <button
              data-testid="product-consulting-link-services"
              onClick={() => scrollToSection("#services")}
              className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brand transition-colors hover:text-white"
            >
              See capabilities
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
